import Page from "@/lib/pages/Page";
import {ActivityIndicator, Modal, StyleSheet, Text, TextInput} from "react-native";
import Nav from "@/lib/components/Nav";
import ActionButton from "@/lib/components/ActionButton";
import PrimaryText from "@/lib/components/PrimaryText";
import {Color} from "@/lib/styles";
import Editable from "@/lib/components/Editable";
import {useChat} from "@/lib/providers/ChatProvider";
import SpinnerPage from "@/lib/pages/SpinnerPage";
import Radio from "@/lib/components/Radio";
import Header from "@/lib/components/Header";
import {useEffect, useState} from "react";
import {useGlobalSearchParams, useRouter} from "expo-router";
import {useSession} from "@/lib/auth/auth";
import {useQuery, useQueryClient} from "@tanstack/react-query";
import {ChatSettings} from "@/lib/types";
import {dataRetryPolicy, fetchBackend, fetchBackendNoContent} from "@/lib/backend";
import ErrorPage from "@/lib/pages/ErrorPage";
import ErrorMessage from "@/lib/components/ErrorMessage";
import Center from "@/lib/components/Center";
import globstyles from "@/lib/globstyles";

export default function Edit() {
    const {currentChat} = useChat();

    const [title, setTitle] = useState("");
    const [pinThread, setPinThread] = useState(false);
    const [lockThread, setLockThread] = useState(false);

    const [editingTitle, setEditingTitle] = useState(false);
    const [titleForEdit, setTitleForEdit] = useState("");

    const [saveError, setSaveError] = useState<Error | null>(null);
    const [busy, setBusy] = useState(false);
    const {chat_id} = useGlobalSearchParams();
    const {session} = useSession();
    const queryClient = useQueryClient();
    const router = useRouter();

    const queryKey = ['chat', chat_id, 'settings', session];
    const {data, isPending, error} = useQuery<ChatSettings>({
        queryKey,
        queryFn: () => fetchBackend(`/chat/${chat_id}/settings`, "GET", session),
        retry: dataRetryPolicy,
        enabled: !!session,
    });

    const saveSettings = async (changes: ChatSettings) => {
        setSaveError(null);
        setBusy(true);
        fetchBackendNoContent(`/chat/${chat_id}/settings`, "POST", session, changes).then(async () => {
            await queryClient.invalidateQueries({queryKey});
            setBusy(false);
            router.dismiss();
        }).catch((error) => {
            setSaveError(error);
            setBusy(false);
        });
    }

    useEffect(() => {
        if (!data) {
            return;
        }
        setTitle(data.title);
        setPinThread(data.pinned);
        setLockThread(data.locked);
    }, [data])

    if (error) {
        return <ErrorPage error={error}/>
    } else if (!currentChat || isPending || !data) {
        return <SpinnerPage/>
    }

    return (
        <Page>
            <Editable color={Color.PrimaryText} onPress={() => {
                setEditingTitle(true);
                setTitleForEdit(title);
            }}>
                <Text style={styles.editableKey}>Tittel</Text>
                <PrimaryText>{title}</PrimaryText>
            </Editable>
            <Radio
                title={"Alltid på topp"}
                checkbox={true}
                selected={pinThread}
                description={"Fest meldingstråd øverst på eventet eller samfunnet."}
                onPress={() => setPinThread(!pinThread)}
            />
            <Header title={"Hvem kan svare"}/>
            <Radio
                title={"Deltakere"}
                checkbox={false}
                selected={!lockThread}
                description={"Deltakere og følgere kan svare."}
                onPress={() => setLockThread(false)}
            />
            <Radio
                title={"Arrangør"}
                checkbox={false}
                selected={lockThread}
                description={"Kun arrangører og moderatorer kan svare."}
                onPress={() => setLockThread(true)}
            />

            {busy && <ActivityIndicator color={Color.PrimaryText} size={"small"}/>}
            {saveError && <ErrorMessage error={saveError}/>}

            <Nav>
                <ActionButton
                    onPress={() => saveSettings({
                        title, locked: lockThread, pinned: pinThread,
                    })}
                    color={Color.PrimaryText}
                    title={"Lagre"}
                />
                <ActionButton
                    onPress={() => {
                    }}
                    color={Color.Red}
                    title={"Slett meldingstråd"}
                />
            </Nav>

            <Modal
                onRequestClose={() => {
                    setEditingTitle(false);
                }}
                visible={editingTitle}
                allowSwipeDismissal={true}
                presentationStyle={"pageSheet"}
                animationType={"slide"}
            >
                <Page>
                    <Center>
                        <Header title={"Gi et navn til meldingstråden"} center={true}/>
                        <TextInput
                            style={globstyles.formTextInput}
                            value={titleForEdit}
                            onSubmitEditing={() => {
                                setEditingTitle(false);
                                setTitle(titleForEdit);
                            }}
                            onChangeText={setTitleForEdit}
                            autoFocus={true}
                        />
                        <Nav>
                            <ActionButton title={"Bruk"} color={Color.Green} onPress={() => {
                                setEditingTitle(false);
                                setTitle(titleForEdit);
                            }}/>
                            <ActionButton title={"Avbryt"} onPress={() => setEditingTitle(false)}/>
                        </Nav>
                    </Center>
                </Page>
            </Modal>
        </Page>
    )
}

export const styles = StyleSheet.create({
    container: {
        borderStyle: "dashed",
        borderRadius: 8,
        borderWidth: 1,
        paddingVertical: 12,
        paddingHorizontal: 12,
        flexWrap: "wrap",
        flexDirection: "row",
        justifyContent: "flex-start",
        alignItems: "flex-start",
        columnGap: 16,
        rowGap: 4,
        width: "100%",
        outlineStyle: "solid",
    },
    editableKey: {
        fontSize: 16,
        fontWeight: 600,
        color: Color.PrimaryText,
    }
})